import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useI18n, type StringKey } from "../i18n";
import {
  addAdjustment, addCompanyHoliday, cancelLeave, decideLeave, loadBalances, loadDirectory, loadHolidays,
  loadLeaveRequests, loadLeaveTypes, removeCompanyHoliday, requestLeave,
  type Balance, type Holiday, type LeaveRequest, type LeaveStatus, type LeaveType,
} from "../data";
import type { DirectoryRow, Workspace } from "../types";

const thisYear = new Date().getFullYear();
const today = () => new Date().toISOString().slice(0, 10);
const tone: Record<LeaveStatus, string> = { pending: "warning", approved: "success", rejected: "danger", cancelled: "neutral" };

export function LeaveStatusBadge({ status }: { status: LeaveStatus }) {
  const { t } = useI18n();
  return <span className={`badge ${tone[status]}`}>{t(`leaveStatus_${status}` as StringKey)}</span>;
}

type Tab = "requests" | "balances" | "holidays";

/** HR's leave page: decide on requests, enter leave, see balances and manage holidays. */
export function Leave({ ws, onOpen }: { ws: Workspace; onOpen: (employmentId: string) => void }) {
  const { t } = useI18n();
  const [tab, setTab] = useState<Tab>("requests");
  return (
    <div className="page">
      <div className="tabs" role="tablist">
        {(["requests", "balances", "holidays"] as Tab[]).map((k) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? "tab current" : "tab"} onClick={() => setTab(k)}>
            {t(k === "requests" ? "tabRequests" : k === "balances" ? "tabBalances" : "tabHolidays")}
          </button>
        ))}
      </div>
      {tab === "requests" && <Requests ws={ws} onOpen={onOpen} />}
      {tab === "balances" && <Balances ws={ws} />}
      {tab === "holidays" && <Holidays ws={ws} />}
    </div>
  );
}

function useLeaveData(ws: Workspace, employmentId?: string) {
  const companyId = ws.membership.company_id;
  const [types, setTypes] = useState<LeaveType[]>([]);
  const [requests, setRequests] = useState<LeaveRequest[] | null>(null);
  const [people, setPeople] = useState<DirectoryRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const refresh = async () => {
    try {
      const [ty, rq, ppl] = await Promise.all([
        loadLeaveTypes(companyId),
        loadLeaveRequests(companyId, employmentId),
        employmentId ? Promise.resolve([] as DirectoryRow[]) : loadDirectory(companyId),
      ]);
      setTypes(ty); setRequests(rq); setPeople(ppl);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };
  useEffect(() => { void refresh(); }, [companyId, employmentId]); // eslint-disable-line react-hooks/exhaustive-deps
  return { types, requests, people, error, setError, refresh };
}

function Requests({ ws, onOpen }: { ws: Workspace; onOpen: (employmentId: string) => void }) {
  const { t, pick } = useI18n();
  const { types, requests, people, error, setError, refresh } = useLeaveData(ws);
  const canManage = ws.permissions.has("leave.manage");
  const [adding, setAdding] = useState(false);
  const [status, setStatus] = useState<"" | LeaveStatus>("");
  const [busy, setBusy] = useState<string | null>(null);
  const typeById = useMemo(() => new Map(types.map((x) => [x.id, x])), [types]);
  const personById = useMemo(() => new Map(people.map((p) => [p.employment_id, p])), [people]);

  async function decide(r: LeaveRequest, approve: boolean) {
    setBusy(r.id);
    setError(null);
    try { await decideLeave(r.id, approve); await refresh(); }
    catch (err) { setError(err instanceof Error ? err.message : t("errorGeneric")); }
    finally { setBusy(null); }
  }
  async function cancel(r: LeaveRequest) {
    setBusy(r.id);
    setError(null);
    try { await cancelLeave(r.id); await refresh(); }
    catch (err) { setError(err instanceof Error ? err.message : t("errorGeneric")); }
    finally { setBusy(null); }
  }

  const pending = (requests ?? []).filter((r) => r.status === "pending");
  const shown = (requests ?? []).filter((r) => !status || r.status === status);
  const name = (id: string) => { const p = personById.get(id); return p ? pick(p.full_name, p.full_name_ar) : "—"; };
  const typeName = (id: string) => { const ty = typeById.get(id); return ty ? pick(ty.name_en, ty.name_ar) : "—"; };

  const row = (r: LeaveRequest, decideButtons: boolean) => (
    <tr key={r.id}>
      <td><button type="button" className="link" onClick={() => onOpen(r.employment_id)}>{name(r.employment_id)}</button></td>
      <td>{typeName(r.leave_type_id)}{r.note && <div className="muted small-note">{r.note}</div>}</td>
      <td className="mono">{r.start_date}{r.end_date !== r.start_date ? ` → ${r.end_date}` : ""}</td>
      <td className="mono">{r.days}</td>
      <td><LeaveStatusBadge status={r.status} /></td>
      {canManage && (
        <td className="cell-actions">
          {decideButtons && r.status === "pending" && (
            <div className="row">
              <button type="button" className="btn primary small" disabled={busy === r.id} onClick={() => decide(r, true)}>{t("approve")}</button>
              <button type="button" className="btn secondary small" disabled={busy === r.id} onClick={() => decide(r, false)}>{t("reject")}</button>
            </div>
          )}
          {!decideButtons && (r.status === "pending" || r.status === "approved") && (
            <button type="button" className="btn secondary small" disabled={busy === r.id} onClick={() => cancel(r)}>{t("cancelRequest")}</button>
          )}
        </td>
      )}
    </tr>
  );

  const head = (
    <thead><tr><th>{t("employee")}</th><th>{t("leaveType")}</th><th>{t("date")}</th><th>{t("days")}</th><th>{t("status")}</th>{canManage && <th />}</tr></thead>
  );

  return (
    <>
      {canManage && !adding && (
        <div className="row"><button type="button" className="btn primary" onClick={() => setAdding(true)}>{t("newRequest")}</button></div>
      )}
      {adding && (
        <LeaveForm ws={ws} types={types} people={people.filter((p) => p.status !== "terminated")} hr
          onDone={(saved) => { setAdding(false); if (saved) void refresh(); }} />
      )}
      {error && <p className="alert danger" role="alert">{error}</p>}
      {requests === null ? <p className="muted">{t("loading")}</p> : (
        <>
          <section className="card">
            <h2>{t("pendingApproval")}</h2>
            {pending.length === 0 ? <p className="muted">{t("noPending")}</p> : (
              <div className="table-wrap"><table>{head}<tbody>{pending.map((r) => row(r, true))}</tbody></table></div>
            )}
          </section>
          <section className="card">
            <div className="card-head">
              <h2>{t("allRequests")}</h2>
              <select id="leave-status" value={status} onChange={(e) => setStatus(e.target.value as "" | LeaveStatus)} aria-label={t("status")}>
                <option value="">{t("allStatuses")}</option>
                {(["pending", "approved", "rejected", "cancelled"] as LeaveStatus[]).map((s) => (
                  <option key={s} value={s}>{t(`leaveStatus_${s}` as StringKey)}</option>
                ))}
              </select>
            </div>
            {shown.length === 0 ? <p className="muted">{t("noRequests")}</p> : (
              <div className="table-wrap"><table>{head}<tbody>{shown.map((r) => row(r, false))}</tbody></table></div>
            )}
          </section>
        </>
      )}
    </>
  );
}

interface FormProps {
  ws: Workspace;
  types: LeaveType[];
  people?: DirectoryRow[];
  employmentId?: string;
  gender?: "female" | "male" | null;
  hr?: boolean;
  onDone: (saved: boolean) => void;
}

export function LeaveForm({ types, people, employmentId, gender, hr, onDone }: FormProps) {
  const { t, pick } = useI18n();
  const [who, setWho] = useState(employmentId ?? "");
  const [typeId, setTypeId] = useState("");
  const [start, setStart] = useState(today());
  const [end, setEnd] = useState(today());
  const [note, setNote] = useState("");
  const [approve, setApprove] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sex = employmentId ? gender : people?.find((p) => p.employment_id === who)?.gender;
  const usable = types.filter((x) => x.active && (x.applies_to === "all" || x.applies_to === sex));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await requestLeave(who, typeId, start, end < start ? start : end, note, !!hr && approve);
      onDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errorGeneric"));
      setBusy(false);
    }
  }

  return (
    <form className="card form-card action-card" onSubmit={submit}>
      <h2>{t("newRequest")}</h2>
      <div className="field-row">
        {people && (
          <label className="field"><span>{t("employee")}</span>
            <select id="lf-who" value={who} onChange={(e) => { setWho(e.target.value); setTypeId(""); }} required>
              <option value="">—</option>
              {people.map((p) => <option key={p.employment_id} value={p.employment_id}>{pick(p.full_name, p.full_name_ar)} · {p.employee_number}</option>)}
            </select></label>
        )}
        <label className="field"><span>{t("leaveType")}</span>
          <select id="lf-type" value={typeId} onChange={(e) => setTypeId(e.target.value)} required>
            <option value="">—</option>
            {usable.map((x) => <option key={x.id} value={x.id}>{pick(x.name_en, x.name_ar)}</option>)}
          </select></label>
      </div>
      <div className="field-row">
        <label className="field"><span>{t("startDate")}</span>
          <input id="lf-start" type="date" value={start} onChange={(e) => { setStart(e.target.value); if (end < e.target.value) setEnd(e.target.value); }} required /></label>
        <label className="field"><span>{t("endDate")}</span>
          <input id="lf-end" type="date" min={start} value={end} onChange={(e) => setEnd(e.target.value)} required /></label>
      </div>
      <label className="field"><span>{t("note")} <em>{t("optional")}</em></span>
        <input id="lf-note" value={note} onChange={(e) => setNote(e.target.value)} /></label>
      {hr && <label className="check"><input id="lf-approve" type="checkbox" checked={approve} onChange={(e) => setApprove(e.target.checked)} /> {t("approveNow")}</label>}
      <p className="muted small-note">{t("leaveRules")}</p>
      {error && <p className="alert danger" role="alert">{error}</p>}
      <div className="actions">
        <button type="button" className="btn secondary" onClick={() => onDone(false)} disabled={busy}>{t("cancel")}</button>
        <button className="btn primary" disabled={busy || !who || !typeId}>{t("submitRequest")}</button>
      </div>
    </form>
  );
}

export function BalanceTable({ balances }: { balances: Balance[] }) {
  const { t, pick } = useI18n();
  return (
    <div className="table-wrap">
      <table>
        <thead><tr><th>{t("leaveType")}</th><th>{t("entitlement")}</th><th className="hide-sm">{t("carriedOver")}</th><th className="hide-sm">{t("adjusted")}</th><th>{t("taken")}</th><th className="hide-sm">{t("pendingDays")}</th><th>{t("remaining")}</th></tr></thead>
        <tbody>
          {balances.map((b) => (
            <tr key={b.leave_type_id}>
              <td>{pick(b.name_en, b.name_ar)}</td>
              <td className="mono">{b.entitlement}</td>
              <td className="mono hide-sm">{b.carried_over}</td>
              <td className="mono hide-sm">{b.adjusted}</td>
              <td className="mono">{b.taken}</td>
              <td className="mono hide-sm">{b.pending}</td>
              <td className="mono strong">{b.remaining}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** One employee's balances, with an adjustment form for HR. Used on the profile. */
export function EmployeeLeave({ ws, employmentId }: { ws: Workspace; employmentId: string }) {
  const { t, pick } = useI18n();
  const [year, setYear] = useState(thisYear);
  const [balances, setBalances] = useState<Balance[] | null>(null);
  const [adjusting, setAdjusting] = useState(false);
  const [adj, setAdj] = useState({ type: "", days: "", reason: "" });
  const [error, setError] = useState<string | null>(null);
  const canManage = ws.permissions.has("leave.manage");

  const refresh = () => loadBalances(employmentId, year).then(setBalances).catch((e) => setError(e.message));
  useEffect(() => { void refresh(); }, [employmentId, year]); // eslint-disable-line react-hooks/exhaustive-deps

  async function save(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await addAdjustment(ws.membership.company_id, employmentId, adj.type, year, Number(adj.days), adj.reason);
      setAdjusting(false);
      setAdj({ type: "", days: "", reason: "" });
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errorGeneric"));
    }
  }

  return (
    <section className="card">
      <div className="card-head">
        <h2>{t("navLeave")}</h2>
        <select id="bal-year" value={year} onChange={(e) => setYear(Number(e.target.value))} aria-label={t("year")}>
          {[thisYear - 1, thisYear, thisYear + 1].map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      {error && <p className="alert danger" role="alert">{error}</p>}
      {balances && <BalanceTable balances={balances} />}
      {canManage && !adjusting && <button type="button" className="link" onClick={() => setAdjusting(true)}>{t("addAdjustment")}</button>}
      {adjusting && balances && (
        <form className="inline-form" onSubmit={save}>
          <div className="field-row">
            <label className="field"><span>{t("leaveType")}</span>
              <select id="adj-type" value={adj.type} onChange={(e) => setAdj({ ...adj, type: e.target.value })} required>
                <option value="">—</option>
                {balances.map((b) => <option key={b.leave_type_id} value={b.leave_type_id}>{pick(b.name_en, b.name_ar)}</option>)}
              </select></label>
            <label className="field"><span>{t("adjustmentDays")}</span>
              <input id="adj-days" type="number" step="0.5" value={adj.days} onChange={(e) => setAdj({ ...adj, days: e.target.value })} required /></label>
          </div>
          <label className="field"><span>{t("adjustmentReason")}</span>
            <input id="adj-reason" value={adj.reason} onChange={(e) => setAdj({ ...adj, reason: e.target.value })} required /></label>
          <div className="actions">
            <button type="button" className="btn secondary" onClick={() => setAdjusting(false)}>{t("cancel")}</button>
            <button className="btn primary" disabled={!adj.type || !adj.days || Number(adj.days) === 0 || !adj.reason.trim()}>{t("save")}</button>
          </div>
        </form>
      )}
    </section>
  );
}

function Balances({ ws }: { ws: Workspace }) {
  const { t, pick } = useI18n();
  const [people, setPeople] = useState<DirectoryRow[] | null>(null);
  const [rows, setRows] = useState<Map<string, Balance[]>>(new Map());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    void loadDirectory(ws.membership.company_id).then((all) => setPeople(all.filter((p) => p.status !== "terminated")));
  }, [ws.membership.company_id]);

  async function loadAll() {
    if (!people) return;
    setBusy(true);
    setError(null);
    try {
      const out = new Map<string, Balance[]>();
      for (let i = 0; i < people.length; i += 8) {
        const chunk = people.slice(i, i + 8);
        const res = await Promise.all(chunk.map((p) => loadBalances(p.employment_id, thisYear)));
        chunk.forEach((p, k) => out.set(p.employment_id, res[k]));
        setRows(new Map(out));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errorGeneric"));
    } finally {
      setBusy(false);
    }
  }

  const keys = ["annual", "casual"];
  const val = (b: Balance[] | undefined, key: string) => b?.find((x) => x.key === key);
  return (
    <section className="card">
      <div className="card-head">
        <h2>{t("tabBalances")} {thisYear}</h2>
        <button type="button" className="btn secondary small" onClick={loadAll} disabled={busy || !people}>{busy ? t("loading") : t("loadBalances")}</button>
      </div>
      {error && <p className="alert danger" role="alert">{error}</p>}
      {people && rows.size > 0 && (
        <div className="table-wrap">
          <table>
            <thead><tr><th>{t("employee")}</th><th>{t("entitlement")}</th><th>{t("taken")}</th><th>{t("remaining")}</th><th className="hide-sm">Casual</th></tr></thead>
            <tbody>
              {people.filter((p) => rows.has(p.employment_id)).map((p) => {
                const b = rows.get(p.employment_id);
                const a = val(b, keys[0]);
                const c = val(b, keys[1]);
                return (
                  <tr key={p.employment_id}>
                    <td>{pick(p.full_name, p.full_name_ar)}</td>
                    <td className="mono">{a ? a.entitlement + a.carried_over + a.adjusted : "—"}</td>
                    <td className="mono">{a ? a.taken : "—"}</td>
                    <td className="mono strong">{a ? a.remaining : "—"}</td>
                    <td className="mono hide-sm">{c ? c.remaining : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Holidays({ ws }: { ws: Workspace }) {
  const { t, pick } = useI18n();
  const companyId = ws.membership.company_id;
  const canManage = ws.permissions.has("settings.manage");
  const [year, setYear] = useState(thisYear);
  const [data, setData] = useState<{ publicDays: Holiday[]; companyDays: Holiday[] } | null>(null);
  const [f, setF] = useState({ date: "", name: "" });
  const [error, setError] = useState<string | null>(null);
  const refresh = () => loadHolidays(companyId, ws.membership.company.country_code, year).then(setData).catch((e) => setError(e.message));
  useEffect(() => { void refresh(); }, [companyId, year]); // eslint-disable-line react-hooks/exhaustive-deps

  async function add(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try { await addCompanyHoliday(companyId, f.date, f.name); setF({ date: "", name: "" }); await refresh(); }
    catch (err) { setError(err instanceof Error ? err.message : t("errorGeneric")); }
  }
  async function remove(date: string) {
    setError(null);
    try { await removeCompanyHoliday(companyId, date); await refresh(); }
    catch (err) { setError(err instanceof Error ? err.message : t("errorGeneric")); }
  }

  return (
    <>
      <div className="row">
        <select id="hol-year" value={year} onChange={(e) => setYear(Number(e.target.value))} aria-label={t("year")}>
          {[thisYear, thisYear + 1].map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      {error && <p className="alert danger" role="alert">{error}</p>}
      <section className="card">
        <h2>{t("publicHolidays")}</h2>
        <ul className="plain-list">
          {(data?.publicDays ?? []).map((h) => (
            <li key={h.holiday_date} className="holiday">
              <span className="mono">{h.holiday_date}</span> {pick(h.name_en, h.name_ar)}
              {h.tentative && <span className="badge warning">{t("tentative")}</span>}
            </li>
          ))}
        </ul>
      </section>
      <section className="card">
        <h2>{t("companyHolidays")}</h2>
        {data && data.companyDays.length === 0 && <p className="muted">{t("noHolidays")}</p>}
        <ul className="plain-list">
          {(data?.companyDays ?? []).map((h) => (
            <li key={h.holiday_date} className="holiday">
              <span className="mono">{h.holiday_date}</span> {pick(h.name_en, h.name_ar)}
              {canManage && <button type="button" className="link" onClick={() => remove(h.holiday_date)}>{t("remove")}</button>}
            </li>
          ))}
        </ul>
        {canManage && (
          <form className="inline-form" onSubmit={add}>
            <div className="field-row">
              <label className="field"><span>{t("date")}</span><input id="hol-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} required /></label>
              <label className="field"><span>{t("holidayName")}</span><input id="hol-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required /></label>
            </div>
            <div className="actions"><button className="btn primary small" disabled={!f.date || !f.name.trim()}>{t("addHoliday")}</button></div>
          </form>
        )}
      </section>
    </>
  );
}

/** Staff self-service: balances, own requests, ask for leave, cancel pending requests. */
export function MyLeave({ ws }: { ws: Workspace }) {
  const { t, pick } = useI18n();
  const employmentId = ws.membership.employment_id!;
  const { types, requests, error, setError, refresh } = useLeaveData(ws, employmentId);
  const [balances, setBalances] = useState<Balance[] | null>(null);
  const [gender, setGender] = useState<"female" | "male" | null>(null);
  const [adding, setAdding] = useState(false);
  const typeById = useMemo(() => new Map(types.map((x) => [x.id, x])), [types]);

  const refreshAll = async () => {
    await refresh();
    setBalances(await loadBalances(employmentId, thisYear));
  };
  useEffect(() => {
    void loadBalances(employmentId, thisYear).then(setBalances).catch((e) => setError(e.message));
    void loadDirectory(ws.membership.company_id).then((d) => setGender(d.find((x) => x.employment_id === employmentId)?.gender ?? null));
  }, [employmentId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function cancel(id: string) {
    setError(null);
    try { await cancelLeave(id); await refreshAll(); }
    catch (err) { setError(err instanceof Error ? err.message : t("errorGeneric")); }
  }

  return (
    <div className="page">
      {!adding && <div className="row"><button type="button" className="btn primary" onClick={() => setAdding(true)}>{t("newRequest")}</button></div>}
      {adding && <LeaveForm ws={ws} types={types} employmentId={employmentId} gender={gender} onDone={(saved) => { setAdding(false); if (saved) void refreshAll(); }} />}
      {error && <p className="alert danger" role="alert">{error}</p>}
      <section className="card">
        <h2>{t("myBalances")} {thisYear}</h2>
        {balances ? <BalanceTable balances={balances} /> : <p className="muted">{t("loading")}</p>}
      </section>
      <section className="card">
        <h2>{t("myRequests")}</h2>
        {requests && requests.length === 0 && <p className="muted">{t("noRequests")}</p>}
        {requests && requests.length > 0 && (
          <div className="table-wrap">
            <table>
              <thead><tr><th>{t("leaveType")}</th><th>{t("date")}</th><th>{t("days")}</th><th>{t("status")}</th><th /></tr></thead>
              <tbody>
                {requests.map((r) => {
                  const ty = typeById.get(r.leave_type_id);
                  return (
                    <tr key={r.id}>
                      <td>{ty ? pick(ty.name_en, ty.name_ar) : "—"}</td>
                      <td className="mono">{r.start_date}{r.end_date !== r.start_date ? ` → ${r.end_date}` : ""}</td>
                      <td className="mono">{r.days}</td>
                      <td><LeaveStatusBadge status={r.status} /></td>
                      <td>{r.status === "pending" && <button type="button" className="btn secondary small" onClick={() => cancel(r.id)}>{t("cancelRequest")}</button>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
